/**
 * The shapes on offer, and how much room each one really has inside it.
 *
 * TWO JOBS, and they belong together because they are the same fact
 * about a shape's outline seen from two sides: what to draw, and where
 * text can go without crossing it.
 *
 * The inset is the part that is easy to get wrong and obvious once it
 * is. A diamond 160 wide holds a box only 80 wide at its middle; an
 * octagon loses its corners; a cylinder loses a band top and bottom to
 * its ellipses. Laying text out against the BOUNDING box puts letters
 * over the outline of every shape that is not a rectangle, which is
 * most of them.
 */

/**
 * The usable rectangle inside a shape, in the shape's own local box.
 *
 * Conservative rather than exact: an inscribed rectangle in an ellipse
 * is w/√2 by h/√2, and using the true maximum leaves text touching the
 * curve at the corners. These are the inscribed figure pulled in far
 * enough that a descender does not graze the outline.
 */
export function textBox(shape, padding = 6) {
  const { w, h } = shape;
  const full = { x: padding, y: padding, w: Math.max(1, w - padding * 2), h: Math.max(1, h - padding * 2) };

  switch (shape.shapeType) {
    case 'ellipse': {
      const iw = w / Math.SQRT2;
      const ih = h / Math.SQRT2;
      return { x: (w - iw) / 2 + padding, y: (h - ih) / 2, w: Math.max(1, iw - padding * 2), h: ih };
    }
    case 'diamond': {
      // The inscribed rectangle of a rhombus is half its diagonals.
      return { x: w / 4 + padding, y: h / 4, w: Math.max(1, w / 2 - padding * 2), h: h / 2 };
    }
    case 'octagon': {
      // The corners are cut at 0.293; text has the full width only
      // across the middle band, so it is inset by the cut.
      const cut = w * 0.18;
      return { x: cut + padding, y: padding, w: Math.max(1, w - (cut + padding) * 2), h: Math.max(1, h - padding * 2) };
    }
    case 'hexagon': {
      const cut = w * 0.25;
      return { x: cut + padding, y: padding, w: Math.max(1, w - (cut + padding) * 2), h: Math.max(1, h - padding * 2) };
    }
    case 'parallelogram': {
      const cut = w * 0.22;
      return { x: cut + padding, y: padding, w: Math.max(1, w - (cut + padding) * 2), h: Math.max(1, h - padding * 2) };
    }
    case 'cylinder': {
      const ry = Math.min(h * 0.16, 22);
      return { x: padding, y: ry, w: Math.max(1, w - padding * 2), h: Math.max(1, h - ry * 2) };
    }
    case 'note': {
      // The folded corner eats the top right.
      const fold = Math.min(22, w / 4, h / 4);
      return { x: padding, y: fold, w: Math.max(1, w - padding * 2 - fold / 2), h: Math.max(1, h - fold - padding) };
    }
    case 'text':
    case 'rectangle':
    case 'rounded':
    default:
      return full;
  }
}

/**
 * The library, as a landman would look through it.
 *
 * Grouped by what the shapes are FOR rather than by geometry, and the
 * chain-of-title group first, because on this canvas that is what almost
 * every shape drawn is going to be. A palette that opens on generic
 * flowchart furniture makes the common case the hardest to reach.
 */
export const SHAPE_LIBRARY = Object.freeze([
  {
    group: 'Chain of title',
    items: [
      {
        key: 'party', label: 'Party', shapeType: 'rectangle',
        w: 160, h: 160, hint: 'A person or company holding an interest',
      },
      {
        key: 'instrument', label: 'Instrument', shapeType: 'text',
        w: 178, h: 109, fontSize: 11, bold: true, fill: 'transparent', stroke: 'transparent',
        text: 'WD 000/000\n00/00/0000',
        hint: 'The deed between two parties — volume, page, date, what moved',
      },
      {
        key: 'life-estate', label: 'Life estate', shapeType: 'rectangle',
        w: 160, h: 160, fill: '#ff80df',
        hint: 'A party holding for life, with the remainder elsewhere',
      },
      {
        key: 'interest', label: 'Interest', shapeType: 'octagon',
        w: 140, h: 150, fill: '#fcfcca', bold: true,
        hint: 'A fraction called out against a tract — 1/12 NPMI, S2NW',
      },
      {
        key: 'note', label: 'Examiner note', shapeType: 'text',
        w: 240, h: 60, fontSize: 16, bold: true, textColor: '#e81313',
        fill: 'transparent', stroke: 'transparent',
        hint: 'Something to check, in red, over the diagram',
      },
    ],
  },
  {
    group: 'Standard',
    items: [
      { key: 'rectangle', label: 'Rectangle', shapeType: 'rectangle', w: 160, h: 90 },
      { key: 'rounded', label: 'Rounded', shapeType: 'rounded', w: 160, h: 90 },
      { key: 'ellipse', label: 'Ellipse', shapeType: 'ellipse', w: 160, h: 100 },
      { key: 'diamond', label: 'Diamond', shapeType: 'diamond', w: 140, h: 140 },
      { key: 'hexagon', label: 'Hexagon', shapeType: 'hexagon', w: 160, h: 100 },
      { key: 'octagon', label: 'Octagon', shapeType: 'octagon', w: 140, h: 140 },
      { key: 'parallelogram', label: 'Parallelogram', shapeType: 'parallelogram', w: 170, h: 90 },
      { key: 'cylinder', label: 'Cylinder', shapeType: 'cylinder', w: 130, h: 150 },
      { key: 'sticky', label: 'Sticky note', shapeType: 'note', w: 150, h: 130, fill: '#fcfcca' },
      {
        key: 'text', label: 'Text', shapeType: 'text', w: 200, h: 40,
        fill: 'transparent', stroke: 'transparent', text: 'Text',
      },
    ],
  },
]);

/**
 * The library, narrowed by a search.
 *
 * Matched against the label, the group and the hint, because somebody
 * looking for the pink box types "life estate" but somebody looking for
 * the yellow one types "fraction" — which is in the hint and nowhere
 * else. Groups with nothing left are dropped rather than shown empty.
 */
export function searchShapes(query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return SHAPE_LIBRARY;
  return SHAPE_LIBRARY
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => (
        `${item.label} ${g.group} ${item.hint || ''} ${item.shapeType}`.toLowerCase().includes(needle)
      )),
    }))
    .filter((g) => g.items.length > 0);
}

/** A library item as the patch that makes the shape, centred on a point. */
export function shapeFrom(item, at) {
  const { key, label, hint, group, ...rest } = item;
  return {
    ...rest,
    x: Math.round(at.x - (item.w || 160) / 2),
    y: Math.round(at.y - (item.h || 90) / 2),
  };
}
